import { observable, configure, action } from 'mobx';
import util from 'preact-util';
import LocalModel from './localModel';

configure({ enforceActions: 'always' });

const LIST_KEYS = ['branchNames', 'disciplineGroups', 'kretsNames'];

// A shooter's watches over the NSF terminliste (backend /api/nsfcompetitionwatches/).
class NsfCompetitionWatchStore extends LocalModel {
    constructor() {
        super('nsfCompetitionWatch', {
            namePlural: 'nsfCompetitionWatches',
            namePluralReal: 'nsfcompetitionwatches',
            sort: '-createdDate',
            limit: 100,
            api: {
                search: {
                    url: '/api/nsfcompetitionwatches/',
                    params: {
                        limit: 100,
                        sort: '-createdDate',
                    },
                },
                load: {
                    url: '/api/nsfcompetitionwatches/',
                    params: {},
                },
                save: {
                    url: '/api/nsfcompetitionwatches/',
                    params: {},
                },
                delete: {
                    url: '/api/nsfcompetitionwatches/',
                    params: {},
                },
            },
        });
    }

    @observable nsfCompetitionWatches = [];

    @observable nsfCompetitionWatch = {};

    @observable newNsfCompetitionWatch = {};

    @observable alerts = [];

    @observable testMatches = [];

    @observable testing = false;

    @observable testSending = false;

    @observable testSendResult = null;

    @observable home = null;

    @observable branchNameOptions = [];

    @observable disciplineGroupOptions = [];

    @observable kretsNameOptions = [];

    @action
    setOptions(filterOptions = {}) {
        this.updateKeyValue('branchNameOptions', filterOptions.branchNames || []);
        this.updateKeyValue('disciplineGroupOptions', filterOptions.disciplineGroups || []);
        this.updateKeyValue('kretsNameOptions', filterOptions.kretsNames || []);
    }

    @action
    setHome(home) {
        this.updateKeyValue('home', home || null);
    }

    @action
    selectWatch(id) {
        const watch = (this.nsfCompetitionWatches || []).find(w => w.id === id) || {};
        this.updateKeyValue('nsfCompetitionWatch', watch);
        this.updateKeyValue('testMatches', []);
    }

    @action
    async loadWatches() {
        try {
            const response = await util.fetchApi('/api/nsfcompetitionwatches/', { method: 'GET' }, {});
            if (response?.status === 200) {
                this.updateKeyValue('nsfCompetitionWatches', response.data || []);
            }
        } catch (err) {
            console.error('nsfCompetitionWatch loadWatches failed', err);
        }
    }

    @action
    async createWatch(input = {}) {
        const response = await util.fetchApi('/api/nsfcompetitionwatches/', { method: 'POST' }, input);
        if (response?.status < 300 && response.data) {
            this.updateKeyValue('nsfCompetitionWatches', [response.data, ...(this.nsfCompetitionWatches || [])]);
            this.updateKeyValue('nsfCompetitionWatch', response.data);
            return response.data;
        }
        return null;
    }

    @action
    async deleteWatch(id) {
        const response = await util.fetchApi(`/api/nsfcompetitionwatches/${id}`, { method: 'DELETE' }, {});
        if (response?.status < 300) {
            this.updateKeyValue('nsfCompetitionWatches', (this.nsfCompetitionWatches || []).filter(w => w.id !== id));
            if (this.nsfCompetitionWatch && this.nsfCompetitionWatch.id === id) {
                this.updateKeyValue('nsfCompetitionWatch', {});
            }
        }
        return response;
    }

    /** Adds or removes one name in a list field and saves the whole array (arrays replace). */
    @action
    async toggleListValue(id, field, name) {
        if (!LIST_KEYS.includes(field)) {
            return null;
        }
        const current = Array.isArray(this.nsfCompetitionWatch[field]) ? [...this.nsfCompetitionWatch[field]] : [];
        const next = current.includes(name) ? current.filter(v => v !== name) : [...current, name];
        this.updateObjectKeyValue('nsfCompetitionWatch', field, next);
        return this.saveField(id, field, next, true);
    }

    /** Both coordinates in ONE PATCH (the backend drops a lone coordinate). */
    @action
    async saveHomePosition(id, homeLat, homeLng) {
        const response = await util.fetchApi(`/api/nsfcompetitionwatches/${id}`, { method: 'PATCH' }, { homeLat, homeLng });
        if (response?.status < 300) {
            if (this.nsfCompetitionWatch && this.nsfCompetitionWatch.id === id) {
                this.updateKeyValue('nsfCompetitionWatch', { ...this.nsfCompetitionWatch, homeLat, homeLng });
            }
            this.updateKeyValue('nsfCompetitionWatches', (this.nsfCompetitionWatches || [])
                .map(w => (w.id === id ? { ...w, homeLat, homeLng } : w)));
        }
        return response;
    }

    @action
    async saveHome({ lat, lng, label = '' }) {
        const response = await util.fetchApi('/api/nsfcompetitionwatches/home', { method: 'POST' }, { lat, lng, label });
        if (response?.status < 300) {
            this.updateKeyValue('home', { lat, lng, label });
        }
        return response;
    }

    @action
    async clearHome() {
        const response = await util.fetchApi('/api/nsfcompetitionwatches/home', { method: 'DELETE' }, {});
        if (response?.status < 300) {
            this.updateKeyValue('home', null);
        }
        return response;
    }

    @action
    async loadAlerts() {
        try {
            const response = await util.fetchApi('/api/nsfcompetitionwatches/alerts', { method: 'GET' }, {});
            if (response?.status === 200) {
                this.updateKeyValue('alerts', response.data || []);
            }
        } catch (err) {
            console.error('nsfCompetitionWatch loadAlerts failed', err);
        }
    }

    /** «Send testvarsel»: a real push + e-mail to me on this watch's channels. */
    @action
    async sendTest(id) {
        this.updateKeyValue('testSending', true);
        this.updateKeyValue('testSendResult', null);
        try {
            const response = await util.fetchApi(`/api/nsfcompetitionwatches/${id}/send-test`, { method: 'POST' }, {});
            if (response?.status === 200) {
                this.updateKeyValue('testSendResult', response.data || {});
            } else {
                this.updateKeyValue('testSendResult', { error: response?.message || 'failed' });
            }
        } catch (err) {
            console.error('nsfCompetitionWatch sendTest failed', err);
            this.updateKeyValue('testSendResult', { error: String(err && err.message ? err.message : err) });
        } finally {
            this.updateKeyValue('testSending', false);
        }
    }

    @action
    async testWatch(id) {
        this.updateKeyValue('testing', true);
        try {
            const response = await util.fetchApi(`/api/nsfcompetitionwatches/${id}/test`, { method: 'POST' }, {});
            if (response?.status === 200) {
                this.updateKeyValue('testMatches', response.data || []);
            }
        } catch (err) {
            console.error('nsfCompetitionWatch testWatch failed', err);
        } finally {
            this.updateKeyValue('testing', false);
        }
    }
}

const store = new NsfCompetitionWatchStore();
export { NsfCompetitionWatchStore, LIST_KEYS };
export default store;
